import { defineStore } from "pinia";

export const useMaintenanceStore = defineStore("maintenanceStore", {
  state: () => ({
    has_maintenance: false,
    has_started: false,
    close_app: false,
    show_in_app: false,
    headline: "",
    further_information: "",
    maintenance_start: "",
    maintenance_end: "",
    upcoming_soon: false,
    upcoming_start: "",
    portal_message: "",
    maintenanceIntervalStarted: false,
    maintenanceInterval: null as ReturnType<typeof setInterval> | null,
  }),

  getters: {
    getMaintenanceMessages(state) {
      return {
        warningMessage: state.portal_message,
        maintenanceMessage: {
          headline: state.headline,
          information: state.further_information,
        },
      };
    },

    isMaintenance(state) {
      return state.has_maintenance && state.has_started && state.show_in_app;
    },

    isMaintenanceClosed(state) {
      return state.has_maintenance && state.has_started && state.close_app;
    },

    isMaintenanceWarning(state) {
      return state.has_maintenance && !state.has_started;
    },

    isUpcomingMaintenanceSoon(state) {
      return state.upcoming_soon && state.show_in_app;
    },
  },

  actions: {
    startMaintenanceInterval() {
      if (!this.maintenanceIntervalStarted) {
        this.fetchMaintenance();
        this.maintenanceIntervalStarted = true;

        this.maintenanceInterval = setInterval(() => {
          this.fetchMaintenance();
        }, 60000);
      }
    },

    stopMaintenanceInterval() {
      if (this.maintenanceIntervalStarted) {
        this.maintenanceIntervalStarted = false;
        clearInterval(this.maintenanceInterval!);
        this.maintenanceInterval = null;
      }
    },

    async fetchMaintenance() {
      try {
        const data = await $fetch("/api/maintenance/get/maintenance");

        const current = data?.current_maintenance?.[0];
        const upcomingList = data?.upcoming_maintenance || [];

        const soonestUpcoming = upcomingList.length
          ? [...upcomingList].sort(
              (a: any, b: any) =>
                a.maintenance_start.epoch - b.maintenance_start.epoch,
            )[0]
          : null;

        const now = Date.now();

        const applyMaintenance = (item: any, isCurrent: boolean) => {
          const start = item.maintenance_start?.epoch * 1000;
          const end = item.maintenance_end?.epoch * 1000;

          this.has_maintenance = true;
          this.has_started = isCurrent ? now >= start && now <= end : false;

          this.close_app = item.flags?.close_portal ?? false;
          this.show_in_app = item.flags?.show_in_portal ?? false;

          this.headline = item.headline || "";
          this.further_information = item.information || "";

          this.maintenance_start = item.maintenance_start?.current || "";
          this.maintenance_end = item.maintenance_end?.current || "";

          this.portal_message =
            item?.pre_messages?.portal ||
            `Please note that Your GoShorty will be going into maintenance on ${this.maintenance_start} until ${this.maintenance_end}.`;

          const minutes = item.maintenance_minutes_until_start;
          this.upcoming_soon = minutes > 0 && minutes <= 20;
          this.upcoming_start = item.maintenance_start?.current || "";
        };

        if (current) {
          applyMaintenance(current, true);

          if (soonestUpcoming) {
            const minutes = soonestUpcoming.maintenance_minutes_until_start;
            this.upcoming_soon = minutes > 0 && minutes <= 20;
            this.upcoming_start =
              soonestUpcoming.maintenance_start?.current || "";
          }

          return;
        }

        if (soonestUpcoming) {
          applyMaintenance(soonestUpcoming, false);
          return;
        }

        this.resetMaintenance();
      } catch (err) {
        console.error("Maintenance fetch failed:", err);
        this.resetMaintenance();
      }
    },

    resetMaintenance() {
      this.has_maintenance = false;
      this.has_started = false;
      this.close_app = false;
      this.show_in_app = false;

      this.headline = "";
      this.further_information = "";
      this.maintenance_start = "";
      this.maintenance_end = "";

      this.upcoming_soon = false;
      this.upcoming_start = "";
    },
  },
});
